import { useState } from 'react';
import { X, Mail, Sparkles, MessageCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Interest = 'personal' | 'enterprise';

type ResultState = 'success' | 'already_registered' | 'already_on_waitlist' | null;

function buildChatIntro(email: string, interest: string, name: string | null): string {
  const segment = interest === 'enterprise' ? 'Enterprise' : 'Personal';
  const who = name ? `I'm ${name} (${email})` : `I'm ${email}`;
  return `Hi Saxa, ${who}. I'm on the waitlist with interest in ${segment} and very interested in xRepl.ai.`;
}

export function WaitlistModal({ isOpen, onClose }: WaitlistModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [interest, setInterest] = useState<Interest>('personal');
  const [result, setResult] = useState<ResultState>(null);
  const [alreadyOnWaitlistData, setAlreadyOnWaitlistData] = useState<{ interest: string; name: string | null; email: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setResult(null);
    setAlreadyOnWaitlistData(null);

    try {
      const response = await fetch(API_ENDPOINTS.WAITLIST, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim() || undefined, interest }),
      });

      const raw = await response.text();
      let data: Record<string, unknown> = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        if (response.ok) {
          alert('Unexpected response from server. Please try again.');
        } else {
          alert('Something went wrong. Please try again.');
        }
        return;
      }

      if (response.ok && data.success) {
        setResult('success');
        setTimeout(() => {
          setResult(null);
          setEmail('');
          setName('');
          setInterest('personal');
          onClose();
        }, 3000);
      } else if (response.ok && (data.alreadyRegistered === true || data.alreadyRegistered === 'true')) {
        setResult('already_registered');
      } else if (response.ok && (data.alreadyOnWaitlist === true || data.alreadyOnWaitlist === 'true')) {
        setResult('already_on_waitlist');
        setAlreadyOnWaitlistData({
          email: (data.email as string) || email,
          interest: (data.interest as string) || interest,
          name: (data.name as string) ?? null,
        });
      } else if (!response.ok) {
        alert((data.error as string) || `Request failed (${response.status}). Please try again.`);
      } else {
        alert((data.error as string) || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Waitlist signup error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const openChatWithSaxa = () => {
    if (!alreadyOnWaitlistData) return;
    const intro = buildChatIntro(alreadyOnWaitlistData.email, alreadyOnWaitlistData.interest, alreadyOnWaitlistData.name);
    const base = `${window.location.origin}${window.location.pathname || '/'}`;
    const sep = base.includes('?') ? '&' : '?';
    window.location.href = `${base}${sep}openChat=1&intro=${encodeURIComponent(intro)}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-white dark:bg-[#0F1F3D] rounded-2xl shadow-2xl border border-gray-200 dark:border-[#1E3A8A]/30 overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        {result === 'already_registered' ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full mb-4">
              <Mail size={32} className="text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Already registered
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This email is already registered. Please contact support for help.
            </p>
            <a
              href="https://xrepl.ai"
              className="inline-block px-4 py-2 text-[#1E3A8A] dark:text-[#60A5FA] font-medium hover:underline"
            >
              Go to support chat on our site →
            </a>
          </div>
        ) : result === 'already_on_waitlist' ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
              <Sparkles size={32} className="text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              You're already on the list
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Keep checking your email for updates, or use the support chat on our site.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Want to chat with Saxa?
            </p>
            <button
              type="button"
              onClick={openChatWithSaxa}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#60A5FA] to-[#1E3A8A] text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <MessageCircle size={20} />
              Chat with Saxa
            </button>
          </div>
        ) : result === 'success' ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              You're on the list! 🎉
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Check your inbox for a welcome email. We'll notify you when xRepl.ai launches.
            </p>
          </div>
        ) : (
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#60A5FA] to-[#1E3A8A] rounded-full mb-4">
                <Sparkles className="text-white" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Coming Soon!
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                xRepl.ai is almost ready. Join our waitlist to be the first to know when we launch!
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0A1628] border border-gray-300 dark:border-[#1E3A8A] rounded-lg focus:ring-2 focus:ring-[#60A5FA] focus:border-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white dark:bg-[#0A1628] border border-gray-300 dark:border-[#1E3A8A] rounded-lg focus:ring-2 focus:ring-[#60A5FA] focus:border-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  I'm interested in
                </span>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="interest"
                      value="personal"
                      checked={interest === 'personal'}
                      onChange={() => setInterest('personal')}
                      className="w-4 h-4 text-[#1E3A8A] focus:ring-[#60A5FA]"
                    />
                    <span className="text-gray-700 dark:text-gray-300">Personal</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="interest"
                      value="enterprise"
                      checked={interest === 'enterprise'}
                      onChange={() => setInterest('enterprise')}
                      className="w-4 h-4 text-[#1E3A8A] focus:ring-[#60A5FA]"
                    />
                    <span className="text-gray-700 dark:text-gray-300">Enterprise</span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#60A5FA] to-[#1E3A8A] text-white font-semibold rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Joining...</span>
                  </>
                ) : (
                  <>
                    <Mail size={20} />
                    <span>Join Waitlist</span>
                  </>
                )}
              </button>
            </form>

            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
              We'll notify you as soon as xRepl.ai is live. Check your inbox for a welcome email. No spam, ever.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
